import React from "react";
import { useTranslation } from "react-i18next";
import berainLogo from "../../../assets/main-logo.png";
import { formatDate } from "../../../utility";
import "../../../style.css";

type PrintHeaderInfo = {
  employeeId?: string;
  employeeName?: string;
  salesOfficeDisplay?: string;
  printedAt?: string;
  printedBy?: string;
};

type ComponentToPrintProps = {
  data: any;
  startDate?: string;
  endDate?: string;
  headerInfo?: PrintHeaderInfo;
};

const formatAreaStreet = (item: { area?: string; street?: string }) => {
  if (item.area && item.street) return `${item.area} / ${item.street}`;
  return item.area ?? item.street ?? "-";
};

const convertDateToDDMMYYYY = (dateString?: string) => {
  if (!dateString) return "-";
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
};

const getAbsoluteLogoUrl = (logoPath: string) => {
  if (/^https?:\/\//i.test(logoPath)) {
    return logoPath;
  }
  const normalizedPath = logoPath.startsWith("/") ? logoPath : `/${logoPath}`;
  return `${window.location.origin}${normalizedPath}`;
};

const ComponentToPrint: React.FC<ComponentToPrintProps> = ({
  data,
  startDate,
  endDate,
  headerInfo,
}) => {
  const { t } = useTranslation();

  return (
    <div id="print-content" className="hidden print:block">
      <div className="flex justify-between items-start mb-6">
        <div className="flex flex-col gap-2">
          {/* <img src={getAbsoluteLogoUrl(berainLogo)} alt="Logo" className="h-12" /> */}
          <p>
            {t("planningReportTableFields.employee_id")}:{" "}
            {headerInfo?.employeeId ?? "-"}
          </p>
          <p>
            {t("planningReportTableFields.employee_name")}:{" "}
            {headerInfo?.employeeName ?? "-"}
          </p>
          <p>
            {t("planningReport.printSalesOffice")}:{" "}
            {headerInfo?.salesOfficeDisplay ?? "-"}
          </p>
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold">{t("planningReport.printTitle")}</h1>
        </div>
        <div className="text-right">
          {startDate === endDate ? (
            <p>Date: {convertDateToDDMMYYYY(startDate)}</p>
          ) : (
            <p>
              Date: {convertDateToDDMMYYYY(startDate)} to{" "}
              {convertDateToDDMMYYYY(endDate)}
            </p>
          )}
          <p>
            {t("planningReport.printedAt")}:{" "}
            {headerInfo?.printedAt ?? "-"}
          </p>
          <p>
            {t("planningReport.printedBy")}:{" "}
            {headerInfo?.printedBy ?? "-"}
          </p>
        </div>
      </div>

      <table className="w-full mb-6 border-collapse border border-gray-300 text-xs">
        <thead>
          <tr>
            <th className="border border-gray-300 p-2 bg-gray-100 text-left">
              {t("planningReportTableFields.sequence_number")}
            </th>
            <th className="border border-gray-300 p-2 bg-gray-100 text-left">
              {t("planningReportTableFields.customer_id")}
            </th>
            <th className="border border-gray-300 p-2 bg-gray-100 text-left">
              {t("planningReportTableFields.delivery_number")}
            </th>
            <th className="border border-gray-300 p-2 bg-gray-100 text-left">
              {t("planningReportTableFields.delivery_date")}
            </th>
            <th className="border border-gray-300 p-2 bg-gray-100 text-left">
              {t("planningReportTableFields.time_slot")}
            </th>
            <th className="border border-gray-300 p-2 bg-gray-100 text-left">
              {t("planningReportTableFields.delivery_type")}
            </th>
            <th className="border border-gray-300 p-2 bg-gray-100 text-left">
              {t("planningReportTableFields.area_street")}
            </th>
            <th className="border border-gray-300 p-2 bg-gray-100 text-left">
              {t("planningReportTableFields.customer_name")}
            </th>
            <th className="border border-gray-300 p-2 bg-gray-100 text-left">
              {t("planningReportTableFields.customer_mobile_number")}
            </th>
          </tr>
        </thead>
        <tbody>
          {data?.items?.map((item: any, index: number) => (
            <tr key={item.delivery_number ?? `${item.emp_id}-${index}`}>
              <td className="border border-gray-300 p-2">{item.sequence_number ?? "-"}</td>
              <td className="border border-gray-300 p-2">{item.customer_id ?? "-"}</td>
              <td className="border border-gray-300 p-2">{item.delivery_number ?? "-"}</td>
              <td className="border border-gray-300 p-2">
                {item.delivery_date ? formatDate(item.delivery_date) : "-"}
              </td>
              <td className="border border-gray-300 p-2">{item.time_slot ?? "-"}</td>
              <td className="border border-gray-300 p-2">{item.delivery_type ?? "-"}</td>
              <td className="border border-gray-300 p-2">{formatAreaStreet(item)}</td>
              <td className="border border-gray-300 p-2">{item.customer_name ?? "-"}</td>
              <td className="border border-gray-300 p-2">{item.customer_mobile ?? "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ComponentToPrint;
